// Companion/ChatBlockContinueOffer.tsx
//
// "Continue with Ora from question N?" above the composer (ORA-1285). The
// student chatted on a question, moved on, and the new question's chat is
// empty: one tap keeps the last chat for the rest of the block. The rule that
// makes the offer lives in `useChatBlockThread`; this only draws it.

import { X } from '@phosphor-icons/react';
import { useSelector, useStore } from 'react-redux';
import { AIActive } from '@/assets/svgs/AIActive';
import {
  markChatBlockOfferDismissed,
  setChatBlockOffer,
  useChatBlockThreadState,
} from '@/chatContext/chatBlockThread';
import { Button } from '@/components/ui/button';
import {
  selectConversationMessageIds,
  type ChatRootState,
} from '@/slices/chat/chatSelectors';
import type { RootState } from '@/store/store';
import type { ChatSurface } from '@/types/chatEnvelope';
import { continueFromOffer } from './chatBlockScopeActions';

interface ChatBlockContinueOfferProps {
  conversationId: string | null;
  surface: ChatSurface;
}

const ChatBlockContinueOffer = ({
  conversationId,
  surface,
}: ChatBlockContinueOfferProps) => {
  const store = useStore<RootState>();
  const { current, scopes, offer } = useChatBlockThreadState();

  const isThreadEmpty = useSelector(
    (state: ChatRootState) =>
      selectConversationMessageIds(state, conversationId).length === 0
  );

  if (
    surface !== 'drawer' ||
    !offer ||
    !current ||
    current.blockId !== offer.blockId ||
    current.index !== offer.atIndex ||
    scopes[offer.blockId] !== 'question' ||
    !isThreadEmpty
  )
    return null;

  return (
    <div
      role="status"
      data-testid="chat-block-continue-offer"
      className="mb-2 flex animate-chat-fade-in items-center gap-2.5 rounded-xl border border-border-subtle bg-surface-muted py-2 pl-3 pr-2"
    >
      <span
        aria-hidden="true"
        className="flex size-4.5 shrink-0 text-green-600 dark:text-green-400 [&>svg]:size-full"
      >
        <AIActive />
      </span>
      <p className="m-0 min-w-0 flex-1 text-xs leading-snug text-fg-primary">
        Continue with Ora from question {offer.fromIndex}?
        <span className="block text-xs text-fg-secondary">
          Keep one chat for the rest of this block.
        </span>
      </p>
      <Button
        type="button"
        size="sm"
        onClick={() => void continueFromOffer(store, offer)}
      >
        Continue
      </Button>
      <Button
        type="button"
        size="icon-sm"
        variant="ghost"
        aria-label="Dismiss"
        onClick={() => {
          markChatBlockOfferDismissed();
          setChatBlockOffer(null);
        }}
      >
        <X size={14} aria-hidden="true" />
      </Button>
    </div>
  );
};

export default ChatBlockContinueOffer;
